/**
 * Error codes and the standard error body (docs/01 §6). Every non-2xx response
 * from apps/api carries `{ error: { code, message, details? } }`.
 *
 * The status listed here is the default the API uses for the code; a handler may
 * pick another status for the same code (e.g. `session_expired` is 401 from the
 * auth middleware and 410 from the queue endpoints). Clients branch on `code`.
 */
export const ErrorCodes = {
  // Generic
  bad_request: 'bad_request',
  validation_error: 'validation_error',
  unauthorized: 'unauthorized',
  forbidden: 'forbidden',
  not_found: 'not_found',
  conflict: 'conflict',
  payload_too_large: 'payload_too_large',
  unsupported_media_type: 'unsupported_media_type',
  rate_limited: 'rate_limited',
  internal: 'internal',
  not_implemented: 'not_implemented',
  upstream_unavailable: 'upstream_unavailable',
  config_error: 'config_error',
  // Auth (docs/03 §1)
  account_deleted: 'account_deleted',
  account_blocked: 'account_blocked',
  // Pairing / devices (docs/03 §2 "TV pairing")
  device_blocked: 'device_blocked',
  pairing_invalid: 'pairing_invalid',
  pairing_expired: 'pairing_expired',
  // Entitlements (docs/07 §1)
  subscription_required: 'subscription_required',
  feature_required: 'feature_required',
  queue_limit_reached: 'queue_limit_reached',
  // Sessions & queue
  session_expired: 'session_expired',
  session_active_elsewhere: 'session_active_elsewhere',
  not_a_member: 'not_a_member',
  duplicate_in_queue: 'duplicate_in_queue',
  tone_not_available: 'tone_not_available',
  song_not_available: 'song_not_available',
  // Billing (docs/03 §2 "Billing")
  voucher_invalid: 'voucher_invalid',
  voucher_expired: 'voucher_expired',
  voucher_already_redeemed: 'voucher_already_redeemed',
  already_subscribed: 'already_subscribed',
  checkout_disabled: 'checkout_disabled',
  // CMS
  package_not_verified: 'package_not_verified',
  entity_in_use: 'entity_in_use',
  // Client-side only (never produced by the API; status 0)
  network_error: 'network_error',
  timeout: 'timeout',
  contract_violation: 'contract_violation',
} as const;

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes];

/** Default HTTP status per code. */
export const ErrorStatus: Record<ErrorCode, number> = {
  bad_request: 400,
  validation_error: 400,
  unauthorized: 401,
  forbidden: 403,
  not_found: 404,
  conflict: 409,
  payload_too_large: 413,
  unsupported_media_type: 415,
  rate_limited: 429,
  internal: 500,
  not_implemented: 501,
  upstream_unavailable: 503,
  config_error: 500,
  account_deleted: 403,
  account_blocked: 403,
  device_blocked: 403,
  pairing_invalid: 404,
  pairing_expired: 410,
  subscription_required: 402,
  feature_required: 403,
  queue_limit_reached: 403,
  session_expired: 401,
  session_active_elsewhere: 409,
  not_a_member: 403,
  duplicate_in_queue: 409,
  tone_not_available: 400,
  song_not_available: 404,
  voucher_invalid: 404,
  voucher_expired: 410,
  voucher_already_redeemed: 409,
  already_subscribed: 409,
  checkout_disabled: 503,
  package_not_verified: 409,
  entity_in_use: 409,
  network_error: 0,
  timeout: 0,
  contract_violation: 0,
};

export const ERROR_CODES: readonly ErrorCode[] = Object.values(ErrorCodes);

export function isErrorCode(value: string): value is ErrorCode {
  return Object.prototype.hasOwnProperty.call(ErrorStatus, value);
}

/** Best-effort code for a response that did not carry a standard body. */
export function codeForStatus(status: number): ErrorCode {
  switch (status) {
    case 400:
      return ErrorCodes.bad_request;
    case 401:
      return ErrorCodes.unauthorized;
    case 402:
      return ErrorCodes.subscription_required;
    case 403:
      return ErrorCodes.forbidden;
    case 404:
      return ErrorCodes.not_found;
    case 409:
      return ErrorCodes.conflict;
    case 413:
      return ErrorCodes.payload_too_large;
    case 415:
      return ErrorCodes.unsupported_media_type;
    case 429:
      return ErrorCodes.rate_limited;
    case 501:
      return ErrorCodes.not_implemented;
    case 502:
    case 503:
    case 504:
      return ErrorCodes.upstream_unavailable;
    default:
      return status >= 500 ? ErrorCodes.internal : ErrorCodes.bad_request;
  }
}

export type ErrorDetails = Record<string, unknown>;

/** Wire shape of every error response. */
export interface ApiErrorBody {
  error: {
    code: ErrorCode | (string & {});
    message: string;
    details?: ErrorDetails;
  };
}

export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null) return false;
  const err = (value as { error?: unknown }).error;
  return (
    typeof err === 'object' &&
    err !== null &&
    typeof (err as { code?: unknown }).code === 'string' &&
    typeof (err as { message?: unknown }).message === 'string'
  );
}

export interface AkpErrorInit {
  status?: number;
  details?: ErrorDetails;
  cause?: unknown;
}

/**
 * The one error type shared by the API (thrown from handlers, serialized by the
 * error middleware) and the client (thrown from every failed call).
 */
export class AkpError extends Error {
  readonly code: ErrorCode | (string & {});
  readonly status: number;
  readonly details: ErrorDetails | undefined;

  constructor(code: ErrorCode | (string & {}), message?: string, init: AkpErrorInit = {}) {
    super(message ?? code, init.cause !== undefined ? { cause: init.cause } : undefined);
    this.name = 'AkpError';
    this.code = code;
    this.status = init.status ?? (isErrorCode(code) ? ErrorStatus[code] : 500);
    this.details = init.details;
  }

  /** Serializes to the standard body. */
  toBody(): ApiErrorBody {
    return {
      error: this.details
        ? { code: this.code, message: this.message, details: this.details }
        : { code: this.code, message: this.message },
    };
  }

  toJSON(): ApiErrorBody {
    return this.toBody();
  }

  /** Builds an error from a response status + parsed JSON body (client side). */
  static fromResponse(status: number, body: unknown, fallbackMessage = 'Request failed'): AkpError {
    if (isApiErrorBody(body)) {
      return new AkpError(body.error.code, body.error.message, {
        status,
        details: body.error.details,
      });
    }
    return new AkpError(codeForStatus(status), fallbackMessage, { status });
  }

  static is(value: unknown): value is AkpError {
    return (
      value instanceof AkpError ||
      (typeof value === 'object' &&
        value !== null &&
        (value as { name?: unknown }).name === 'AkpError' &&
        typeof (value as { code?: unknown }).code === 'string')
    );
  }
}

export function isAkpError(value: unknown, code?: ErrorCode): value is AkpError {
  return AkpError.is(value) && (code === undefined || value.code === code);
}
